<template>
  <section>
    <div class="mb-2">
      <b-row
        v-if="!isReadOnlyOrNoManage || canDownloadItems"
        class="mb-1"
      >
        <b-col class="d-flex justify-content-end">
          <e-button
            v-if="canDownloadItems"
            id="download-items"
            class="mr-1"
            :text="$t('Baixar Planilha')"
            variant="primary"
            icon="download"
            :busy="downloading"
            @click="onDownloadItems"
          />
          <e-button
            v-if="!isReadOnlyOrNoManage"
            :text="$t('Adicionar item')"
            variant="primary"
            @click="onAddItem"
          />
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <FormulateForm ref="formulateOrderItems">
            <b-table
              ref="productTable"
              show-empty
              striped
              class="bordered"
              :fields="fields"
              :items="items"
              style="max-height: 500px !important"
            >
              <template #cell(action)="row">
                <e-grid-actions
                  :show-update="false"
                  :show-delete="!readOnly"
                  @delete="onRemoveItem(row.item)"
                />
              </template>

              <template #cell(quantity)="row">
                <div style="display: flex; flex-direction: column; align-items: center">
                  <FormulateInput
                    v-if="!isReadOnlyOrNoManage"
                    :id="`order-items-quantity-${row.index}`"
                    v-model="row.item.quantity"
                    type="text-number"
                    :precision="0"
                    class="text-right"
                    :title="$t('Clique para editar')"
                    validation="required|min:1"
                    :validation-messages="{
                      required: $t('Obrigatório'),
                      min: $t('Deve ser maior que zero'),
                    }"
                    style="width: 80px"
                  />
                  <span v-else>{{ row.item.quantity | number(0) }}</span>
                </div>
              </template>

              <template #cell(packs)="row">
                <span>{{ getPackQuantity(row.item) }}</span>
              </template>

              <template #head(adjustedQuantity)="data">
                <span>{{ data.label }}</span>
                <e-instructions
                  container-class="ml-1"
                  target="instruction_adjusted_quantity"
                  :title="$t('Qtd. Ajustada')"
                  :instruction="
                    $t(
                      'Quantidade de packs arredondada para cima, ou seja, o total de packs inteiros necessários para atender a quantidade solicitada.'
                    )
                  "
                />
              </template>

              <template #cell(adjustedQuantity)="row">
                <span>{{ getAdjustedQuantity(row.item) }}</span>
              </template>
            </b-table>
          </FormulateForm>
        </b-col>
      </b-row>
    </div>
  </section>
</template>

<script>
import _ from 'lodash'
import { mapGetters, mapActions } from 'vuex'
import { BRow, BCol, BTable } from 'bootstrap-vue'
import { EButton, EGridActions, EInstructions } from '@/views/components'
import { alerts, downloader } from '@/mixins'
import {
  getInitialPurchaseRequisition,
  getInitialPurchaseRequisitionItem
} from '@/store/pages/purchase/purchase-requisition/purchase-requisition-maintain'
import { roundDecimal } from '@/utils/number-utils'

export default {
  name: 'OrderItems',

  components: {
    BRow,
    BCol,
    BTable,
    EButton,
    EGridActions,
    EInstructions,
  },

  mixins: [alerts, downloader],

  props: {
    isXml: {
      type: Boolean,
      required: true,
    },
    purchaseRequisition: {
      type: Object,
      required: true,
    },
    canManage: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      saving: false,
      downloading: false,
      productOptions: [],
      updateIndex: null,
      sku: null,
      skus: [],
      localPurchaseRequisition: getInitialPurchaseRequisition(),
    }
  },

  computed: {
    ...mapGetters('pages/purchase/purchaseRequisition/purchaseRequisitionMaintain', ['readOnly']),    
    
    /**
     * A planilha é gerada pela API a partir da requisição persistida, portanto só fica
     * disponível depois que ela é salva.
     */
    canDownloadItems() {
      return !!this.purchaseRequisition?.id
    },

    isReadOnlyOrNoManage() {
      return this.readOnly || !this.canManage
    },    
    
    skusOptions() {
      return this.skus.map(sku => ({
        ...sku,
        value: sku.id,
        label: sku.name,
      }))
    },

    fields() {
      return [
        {
          label: this.$t('Ações'),
          key: 'action',
          class: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Produto'),
          key: 'sku.name',
          hide: !this.isXml,
        },
        {
          label: this.$t('Unid.'),
          key: 'sku.unitOfMeasurement',
          class: 'text-center',
          hide: !this.isXml,
        },
        {
          label: this.$t('Estoque'),
          key: 'quantityInStock',
          class: 'text-center',
          formatter: value => this.$options.filters.number(value, 0, true),
        },
        {
          label: this.$t('Qtd.'),
          key: 'quantity',
          class: 'text-center',
          formatter: value => this.$options.filters.number(value, 3, true),
        },
        {
          label: this.$t('Qtd. Packs'),
          key: 'packs',
          class: 'text-center',
          thStyle: { width: '120px' },
        },
        {
          label: this.$t('Qtd. Ajustada'),
          key: 'adjustedQuantity',
          class: 'text-center',
          thStyle: { width: '140px' },
        },
      ].filter(a => !a.hide)
    },
    items() {
      return this.purchaseRequisition?.items.filter(i => !i.isDeleted)
    },
  },

  methods: {
    ...mapActions('pages/purchase/purchaseRequisition/purchaseRequisitionMaintain', [
      'removeItem',
      'generateItemsReport',
    ]),

    async onDownloadItems() {
      try {
        this.downloading = true
        const reportData = await this.generateItemsReport(this.purchaseRequisition.id)

        if (reportData) {
          this.forceFileDownloadXlsx(
            reportData,
            `Requisicao_${this.purchaseRequisition.id}_-Loja_${this.purchaseRequisition?.store?.id}`
          )
        } else {
          this.showError({ message: this.$t('Não foi possível recuperar os dados do relatório') })
        }
      } catch (error) {
        this.showError({ error })
      } finally {
        this.downloading = false
      }
    },
    createNewProduct(productItem, index) {
      this.$router.push({
        name: 'purchase-order-xml-import-add-product',
        params: {
          callbackRouteName: this.$route.name,
          purchaseRequisitionData: {
            purchaseRequisition: this.purchaseRequisition,
            itemIndex: index,
          },
        },
      })
    },

    selectSku(product, skuIdSelected) {
      // eslint-disable-next-line no-param-reassign
      product.erpSku.sku = this.skus.find(sku => sku.id === parseInt(skuIdSelected, 10))
      // eslint-disable-next-line no-param-reassign
      product.erpSku.SkuInputMethod = 'Manual'

      if (!this.readOnly) {
        this.$emit('update-average-prices')
      }
    },

    async deleteSkuAssociation(sku) {
      if (
        await this.confirm({
          icon: 'question',
          text: this.$t('Tem certeza que deseja remover esta associação?'),
        })
      ) {
        const { erpSku } = getInitialPurchaseRequisitionItem()
        /* eslint-disable no-param-reassign */
        sku.sku = null
        sku.skuSearched = null
        sku.validationAvgPrice = erpSku.validationAvgPrice
        sku.price = erpSku.price
        sku.averagePrice = erpSku.averagePrice
        /* eslint-disable no-param-reassign */
      }
    },

    async fetchSearchSku(loading, search) {
      try {
        loading(true)
        this.skus = (
          await this.$http.get('/api/skus/search', {
            params: {
              search,
              active: true,
            },
          })
        ).data.results
      } catch (e) {
        this.showError({
          message: this.$t('Não foi possível fazer a pesquisa'),
        })
      } finally {
        loading(false)
      }
    },

    /**
     * Calcula a quantidade de packs correspondente à quantidade solicitada,
     * considerando as unidades por pack do produto (Product.UnitsPerPack).
     * Retorna null quando o produto não possui unidades por pack informadas.
     */
    calculatePacks(item) {
      const unitsPerPack = item.unitsPerPack ?? item.sku?.product?.unitsPerPack

      if (!unitsPerPack || unitsPerPack <= 0) {
        return null
      }

      return Number(item.quantity || 0) / unitsPerPack
    },

    getPackQuantity(item) {
      const packs = this.calculatePacks(item)

      return packs === null ? '-' : this.$options.filters.number(packs, 2, true)
    },

    /**
     * Quantidade de packs arredondada para cima, ou seja, o total de packs
     * inteiros necessários para atender a quantidade solicitada.
     */
    getAdjustedQuantity(item) {
      const packs = this.calculatePacks(item)

      return packs === null ? '-' : this.$options.filters.number(Math.ceil(packs), 0, true)
    },

    onAddItem() {
      this.$emit('add-item')
    },
    async onRemoveItem(item) {
      if (this.readOnly || !this.canManage) {
        this.showError({
          message: this.$t('Você não tem permissão para remover itens'),
        })
        return
      }
      if (
        await this.confirm({
          icon: 'question',
          text: this.$t('Tem certeza que deseja remover este item?'),
        })
      ) {
        this.removeItem(item)
      }
    },

    onInputErpQuantity(item) {
      // eslint-disable-next-line no-param-reassign
      item.erpSku.quantityInputMethod = 'Manual'

      if (!this.isXml) {
        // eslint-disable-next-line no-param-reassign
        item.totalValue = roundDecimal(item.erpSku.quantity * item.unitValue, 5)
      }
    },
    onInputErpUnitValue(item) {
      if (!this.isXml) {
        // eslint-disable-next-line no-param-reassign
        item.totalValue = roundDecimal(item.erpSku.quantity * item.unitValue, 5)
      }
    },

    validUnitValue(item) {
      if (this.readOnly || item.erpSku?.averagePrice === null) return true

      const minValue = item.erpSku?.validationAvgPrice?.minValue
      const maxValue = item.erpSku?.validationAvgPrice?.maxValue
      if (_.isNil(minValue) || _.isNil(maxValue)) return true

      const unitValue = this.isXml
        ? roundDecimal(item.totalValue / item.erpSku.quantity, 2)
        : item.unitValue

      return unitValue >= minValue && unitValue <= maxValue
    },

    validateForm() {
      this.$refs.formulateOrderItems.showErrors()
      return this.$refs.formulateOrderItems.hasErrors
    },
  },
}
</script>

<style>
.echope-product {
  background-color: #e7e7e7 !important;
}
</style>
